import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Camera,
  Upload,
  Loader2,
  Trash2,
  Save,
  ImagePlus,
  X,
  Check,
  AlertTriangle,
  Pencil,
  Eraser,
  CloudOff,
  CloudUpload,
  Clock,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoiceInputButton } from "@/components/voice-input-button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { PhotoApi, type ServerPhotoMetadata } from "@/lib/photo-api";
import { PhotoAnnotator } from "@/components/photos/photo-annotator";
import FacadeReportExport from "@/components/project/facade-report-export";
import { compressImageFile } from "@/lib/image-compression";
import {
  enqueuePhoto,
  isLikelyNetworkError,
  removePending,
  updatePendingDescription,
  type PendingPhotoRecord,
} from "@/lib/offline-photo-queue";
import { useOfflinePhotoQueue } from "@/hooks/use-offline-photo-queue";
import type { Project } from "@shared/schema";

interface MobileProjectEditProps {
  projectId: string;
}

const MAX_FILE_BYTES = 18 * 1024 * 1024;

interface UploadFailure {
  name: string;
  error: string;
}

interface UploadProgress {
  total: number;
  completed: number;
  inFlightIndex: number;
  currentName: string;
  phase: "compress" | "upload";
}

type GalleryItem =
  | { kind: "server"; id: string; photo: ServerPhotoMetadata }
  | { kind: "pending"; id: string; record: PendingPhotoRecord; objectUrl: string };

type OpenItem =
  | { kind: "server"; photo: ServerPhotoMetadata }
  | { kind: "pending"; record: PendingPhotoRecord; objectUrl: string };

export function MobileProjectEdit({ projectId }: MobileProjectEditProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);

  // iOS Safari pushes a history entry when the camera opens (so the native
  // back gesture can dismiss it). When the user confirms or cancels the photo,
  // iOS pops that entry, firing a `popstate` event. Wouter listens to
  // `popstate` and would navigate the app back to the previous page (dashboard).
  //
  // Fix strategy:
  //   1. Store the current page URL just before opening the camera.
  //   2. Register a capture-phase `popstate` listener so it runs BEFORE
  //      wouter's bubble-phase listener.
  //   3. On the first post-camera `popstate`, stop propagation (preventing
  //      wouter from navigating away) and push the saved URL back onto the
  //      history stack.
  const cameraOpenedRef = useRef(false);
  const preCameraHrefRef = useRef<string>("");

  function openCamera() {
    preCameraHrefRef.current = window.location.href;
    cameraOpenedRef.current = true;
    cameraInputRef.current?.click();
  }

  function openGallery() {
    galleryInputRef.current?.click();
  }

  useEffect(() => {
    function handlePopState(e: PopStateEvent) {
      if (!cameraOpenedRef.current) return;
      cameraOpenedRef.current = false;
      // Prevent wouter's own popstate listener from navigating away.
      e.stopImmediatePropagation();
      // Restore the page URL we were on before the camera opened.
      window.history.pushState(null, "", preCameraHrefRef.current);
    }

    function handleWindowFocus() {
      // Safety reset: some browsers (e.g. certain Android Chrome versions)
      // do not fire `popstate` or `change` when the camera is canceled.
      // When the window regains focus after camera close, reset the guard so
      // a subsequent legitimate back-navigation is NOT accidentally blocked.
      if (!cameraOpenedRef.current) return;
      // Use a short delay to give `popstate` (if any) a chance to run first.
      setTimeout(() => {
        cameraOpenedRef.current = false;
      }, 200);
    }

    // capture: true → our handler runs before wouter's bubble-phase handler.
    window.addEventListener("popstate", handlePopState, { capture: true });
    window.addEventListener("focus", handleWindowFocus);
    return () => {
      window.removeEventListener("popstate", handlePopState, { capture: true });
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, []);

  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
    null,
  );
  const [uploadFailures, setUploadFailures] = useState<UploadFailure[]>([]);
  const isUploading = uploadProgress !== null;
  const [openItemKey, setOpenItemKey] = useState<string | null>(null);
  const [draftDesc, setDraftDesc] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: "server"; id: number }
    | { kind: "pending"; id: number }
    | null
  >(null);
  const [annotatingPhotoId, setAnnotatingPhotoId] = useState<number | null>(null);
  const [removeAnnotationTargetId, setRemoveAnnotationTargetId] = useState<
    number | null
  >(null);
  // Cache-Buster pro Foto, damit eine frisch gespeicherte Annotation sofort
  // statt der gecachten Version angezeigt wird.
  const [photoVersions, setPhotoVersions] = useState<Record<number, number>>({});

  const [projectNote, setProjectNote] = useState("");
  const [projectNoteSaved, setProjectNoteSaved] = useState<string>("");
  const [savingNote, setSavingNote] = useState(false);

  const [savingDraftDesc, setSavingDraftDesc] = useState(false);

  // ---------------------------------------------------------------------------
  // Project metadata
  // ---------------------------------------------------------------------------
  const { data: project, isLoading: projectLoading } = useQuery<Project>({
    queryKey: ["/api/projects", projectId],
    enabled: !!projectId,
  });

  useEffect(() => {
    if (project) {
      const note = project.description ?? "";
      setProjectNote(note);
      setProjectNoteSaved(note);
    }
  }, [project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const noteDirty = projectNote !== projectNoteSaved;

  const saveNoteMutation = useMutation({
    mutationFn: async (description: string) => {
      const res = await apiRequest("PUT", `/api/projects/${projectId}`, {
        description,
      });
      return (await res.json()) as Project;
    },
    onMutate: () => setSavingNote(true),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["/api/projects"] });
      qc.setQueryData(["/api/projects", projectId], updated);
      setProjectNoteSaved(updated.description ?? "");
      toast({ title: "Notiz gespeichert" });
    },
    onError: () => {
      toast({
        title: "Notiz konnte nicht gespeichert werden",
        variant: "destructive",
      });
    },
    onSettled: () => setSavingNote(false),
  });

  // ---------------------------------------------------------------------------
  // Photos (Server + Offline-Queue)
  // ---------------------------------------------------------------------------
  const photosQueryKey = ["/api/projects", projectId, "photos"] as const;
  const { data: photos = [], isLoading: photosLoading } = useQuery<
    ServerPhotoMetadata[]
  >({
    queryKey: photosQueryKey,
    queryFn: () => PhotoApi.listPhotos(projectId),
    enabled: !!projectId,
  });

  const offlineQueue = useOfflinePhotoQueue(projectId);

  // Stabile Object-URLs pro Pending-Foto, die beim Entfernen wieder freigegeben werden.
  const objectUrlsRef = useRef<Map<number, string>>(new Map());
  const [pendingUrls, setPendingUrls] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    const cache = objectUrlsRef.current;
    const next = new Map<number, string>();
    const seen = new Set<number>();
    for (const record of offlineQueue.pending) {
      seen.add(record.id);
      const existing = cache.get(record.id);
      if (existing) {
        next.set(record.id, existing);
      } else {
        const url = URL.createObjectURL(record.blob);
        cache.set(record.id, url);
        next.set(record.id, url);
      }
    }
    for (const [id, url] of Array.from(cache.entries())) {
      if (!seen.has(id)) {
        URL.revokeObjectURL(url);
        cache.delete(id);
      }
    }
    setPendingUrls(next);
  }, [offlineQueue.pending]);

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      objectUrlsRef.current.clear();
    };
  }, []);

  const galleryItems = useMemo<GalleryItem[]>(() => {
    const pendingItems: GalleryItem[] = offlineQueue.pending.map((record) => ({
      kind: "pending",
      id: `pending-${record.id}`,
      record,
      objectUrl: pendingUrls.get(record.id) ?? "",
    }));
    const serverItems: GalleryItem[] = photos.map((photo) => ({
      kind: "server",
      id: `server-${photo.id}`,
      photo,
    }));
    return [...pendingItems, ...serverItems];
  }, [offlineQueue.pending, pendingUrls, photos]);

  const openItem = useMemo<OpenItem | null>(() => {
    if (!openItemKey) return null;
    const found = galleryItems.find((item) => item.id === openItemKey);
    if (!found) return null;
    if (found.kind === "server") return { kind: "server", photo: found.photo };
    return {
      kind: "pending",
      record: found.record,
      objectUrl: found.objectUrl,
    };
  }, [openItemKey, galleryItems]);

  useEffect(() => {
    if (openItem) {
      const desc =
        openItem.kind === "server"
          ? typeof openItem.photo.description === "string"
            ? openItem.photo.description
            : ""
          : openItem.record.description;
      setDraftDesc(desc);
    }
  }, [openItem?.kind, openItem?.kind === "server" ? openItem.photo.id : openItem?.record.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Upload-Handler (mit Offline-Fallback)
  // ---------------------------------------------------------------------------
  async function uploadOrQueueOne(file: File): Promise<"uploaded" | "queued"> {
    if (file.size > MAX_FILE_BYTES) {
      throw new Error(
        `${file.name} ist zu groß (max. ${(MAX_FILE_BYTES / 1024 / 1024) | 0} MB).`,
      );
    }
    const queueOffline = async () => {
      await enqueuePhoto({
        projectId,
        blob: file,
        fileName: file.name || `foto-${Date.now()}.jpg`,
        mimeType: file.type || "image/jpeg",
        fileSize: file.size,
      });
    };

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      await queueOffline();
      return "queued";
    }
    try {
      await PhotoApi.uploadPhoto(projectId, file);
      return "uploaded";
    } catch (err) {
      if (isLikelyNetworkError(err) || (typeof navigator !== "undefined" && navigator.onLine === false)) {
        await queueOffline();
        return "queued";
      }
      throw err;
    }
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    if (isUploading) return;
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) {
      toast({
        title: "Nur Bilder erlaubt",
        description: "Bitte ein Foto oder Bilddatei auswählen.",
        variant: "destructive",
      });
      return;
    }
    const tooLarge: UploadFailure[] = [];
    const acceptable: File[] = [];
    for (const file of list) {
      if (file.size > MAX_FILE_BYTES) {
        tooLarge.push({
          name: file.name,
          error: `Zu groß (max. ${(MAX_FILE_BYTES / 1024 / 1024) | 0} MB).`,
        });
      } else {
        acceptable.push(file);
      }
    }

    setUploadFailures([]);

    if (acceptable.length === 0) {
      setUploadFailures(tooLarge);
      toast({
        title: "Keine Fotos hochgeladen",
        description: "Alle ausgewählten Dateien waren zu groß.",
        variant: "destructive",
      });
      return;
    }

    setUploadProgress({
      total: acceptable.length,
      completed: 0,
      inFlightIndex: 0,
      currentName: acceptable[0].name,
      phase: "compress",
    });

    // Vor dem Upload werden die Fotos im Browser auf eine sinnvolle Maximalgröße
    // verkleinert. Das spart vor allem auf der Baustelle mit mobilem Netz Zeit
    // und Datenvolumen, ohne die Qualität für PDF-Angebote spürbar zu reduzieren.
    const prepared: File[] = [];
    for (let i = 0; i < acceptable.length; i++) {
      const original = acceptable[i];
      setUploadProgress({
        total: acceptable.length,
        completed: i,
        inFlightIndex: i,
        currentName: original.name,
        phase: "compress",
      });
      const compressed = await compressImageFile(original);
      prepared.push(compressed);
    }

    setUploadProgress({
      total: acceptable.length,
      completed: acceptable.length,
      inFlightIndex: acceptable.length - 1,
      currentName: acceptable[acceptable.length - 1].name,
      phase: "compress",
    });

    setUploadProgress({
      total: prepared.length,
      completed: 0,
      inFlightIndex: 0,
      currentName: prepared[0].name,
      phase: "upload",
    });

    let uploaded = 0;
    let queued = 0;
    const failures: UploadFailure[] = [];

    for (let i = 0; i < prepared.length; i++) {
      const file = prepared[i];
      setUploadProgress({
        total: prepared.length,
        completed: i,
        inFlightIndex: i,
        currentName: file.name,
        phase: "upload",
      });
      try {
        const result = await uploadOrQueueOne(file);
        if (result === "uploaded") uploaded += 1;
        else queued += 1;
      } catch (err) {
        failures.push({
          name: file.name,
          error: err instanceof Error ? err.message : "Unbekannter Fehler",
        });
      }
      setUploadProgress({
        total: prepared.length,
        completed: i + 1,
        inFlightIndex: i + 1,
        currentName: file.name,
        phase: "upload",
      });
    }


    await new Promise((r) => setTimeout(r, 350));
    setUploadProgress(null);

    const allFailures: UploadFailure[] = [...tooLarge, ...failures];
    setUploadFailures(allFailures);

    if (uploaded > 0) {
      qc.invalidateQueries({ queryKey: photosQueryKey });
    }

    const totalAttempted = uploaded + queued + allFailures.length;

    if (allFailures.length === 0 && queued === 0 && uploaded > 0) {
      toast({
        title: uploaded === 1 ? "Foto hochgeladen" : `${uploaded} Fotos hochgeladen`,
      });
    } else if (allFailures.length === 0 && uploaded === 0 && queued > 0) {
      toast({
        title:
          queued === 1
            ? "Foto offline gespeichert"
            : `${queued} Fotos offline gespeichert`,
        description: "Wird automatisch hochgeladen, sobald wieder Internet da ist.",
      });
    } else if (allFailures.length === 0 && uploaded > 0 && queued > 0) {
      toast({
        title: `${uploaded} hochgeladen, ${queued} in der Warteschlange`,
        description: "Die Restlichen werden bei Internetverbindung nachgereicht.",
      });
    } else if (uploaded + queued > 0) {
      const okPart =
        queued === 0
          ? `${uploaded} von ${totalAttempted}`
          : `${uploaded + queued} von ${totalAttempted}`;
      toast({
        title: `${okPart} Fotos verarbeitet`,
        description: `${allFailures.length} ${
          allFailures.length === 1 ? "Foto" : "Fotos"
        } fehlgeschlagen – siehe Liste unten.`,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Upload fehlgeschlagen",
        description: "Kein Foto konnte gespeichert werden – siehe Liste unten.",
        variant: "destructive",
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Mutationen für Server-Fotos
  // ---------------------------------------------------------------------------
  const updatePhotoMutation = useMutation({
    mutationFn: async ({ id, description }: { id: number; description: string }) => {
      await PhotoApi.updateMetadata(id, { description });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: photosQueryKey });
      toast({ title: "Beschreibung gespeichert" });
      setOpenItemKey(null);
    },
    onError: () => {
      toast({
        title: "Speichern fehlgeschlagen",
        variant: "destructive",
      });
    },
  });

  const saveAnnotationMutation = useMutation({
    mutationFn: async ({ id, dataUrl }: { id: number; dataUrl: string }) => {
      await PhotoApi.saveAnnotation(id, dataUrl);
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: photosQueryKey });
      setPhotoVersions((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));
      setAnnotatingPhotoId(null);
      toast({ title: "Markierung gespeichert" });
    },
    onError: () => {
      toast({
        title: "Markierung konnte nicht gespeichert werden",
        variant: "destructive",
      });
    },
  });

  const removeAnnotationMutation = useMutation({
    mutationFn: async (id: number) => {
      await PhotoApi.saveAnnotation(id, null);
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: photosQueryKey });
      setPhotoVersions((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));
      setRemoveAnnotationTargetId(null);
      toast({ title: "Markierungen entfernt" });
    },
    onError: () => {
      toast({
        title: "Markierungen konnten nicht entfernt werden",
        variant: "destructive",
      });
    },
  });

  function photoDisplayUrl(p: ServerPhotoMetadata): string {
    const v = photoVersions[p.id];
    const base = p.hasAnnotation
      ? PhotoApi.getAnnotatedUrl(p.id)
      : PhotoApi.getPhotoUrl(p.id);
    return v ? `${base}?v=${v}` : base;
  }

  const deletePhotoMutation = useMutation({
    mutationFn: async (id: number) => {
      await PhotoApi.deletePhoto(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: photosQueryKey });
      toast({ title: "Foto gelöscht" });
      setDeleteTarget(null);
      setOpenItemKey(null);
    },
    onError: () => {
      toast({
        title: "Löschen fehlgeschlagen",
        variant: "destructive",
      });
    },
  });

  // ---------------------------------------------------------------------------
  // Pending-Aktionen
  // ---------------------------------------------------------------------------
  async function handleSavePendingDescription(record: PendingPhotoRecord, description: string) {
    setSavingDraftDesc(true);
    try {
      await updatePendingDescription(record.id, description);
      toast({ title: "Beschreibung gespeichert" });
      setOpenItemKey(null);
    } catch (err) {
      toast({
        title: "Speichern fehlgeschlagen",
        description: err instanceof Error ? err.message : "Unbekannter Fehler.",
        variant: "destructive",
      });
    } finally {
      setSavingDraftDesc(false);
    }
  }

  async function handleRemovePending(id: number) {
    try {
      await removePending(id);
      toast({ title: "Aus Warteschlange entfernt" });
      setDeleteTarget(null);
      setOpenItemKey(null);
    } catch (err) {
      toast({
        title: "Entfernen fehlgeschlagen",
        description: err instanceof Error ? err.message : "Unbekannter Fehler.",
        variant: "destructive",
      });
    }
  }

  async function handleManualSync() {
    try {
      const res = await offlineQueue.flushNow();
      if (res.uploaded > 0) {
        qc.invalidateQueries({ queryKey: photosQueryKey });
        toast({
          title:
            res.uploaded === 1
              ? "1 Foto hochgeladen"
              : `${res.uploaded} Fotos hochgeladen`,
        });
      } else if (res.failed > 0) {
        toast({
          title: "Upload fehlgeschlagen",
          description:
            "Bitte Internetverbindung prüfen oder später erneut versuchen.",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Synchronisation fehlgeschlagen",
        description: err instanceof Error ? err.message : "Unbekannter Fehler.",
        variant: "destructive",
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (projectLoading) {
    return (
      <div className="px-4 py-10 text-center text-gray-600">
        <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
        Projekt wird geladen…
      </div>
    );
  }

  if (!project) {
    return (
      <div className="px-4 py-10 text-center text-gray-600">
        <p className="mb-4">Projekt nicht gefunden.</p>
        <Button onClick={() => navigate("/dashboard")}>Zurück</Button>
      </div>
    );
  }

  const pendingCount = offlineQueue.count;
  const showOfflineHint = !offlineQueue.online;

  return (
    <div className="max-w-xl mx-auto">
      {/* Sticky page header: back button + live status badge */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 py-1.5 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="-ml-2 py-2.5 h-auto text-gray-700"
          data-testid="button-mobile-edit-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Projekte
        </Button>

        <div className="flex items-center gap-2">
          {uploadFailures.length > 0 && (
            <div
              className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 text-red-700"
              data-testid="upload-error-header-badge"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {uploadFailures.length === 1
                ? "1 Fehler"
                : `${uploadFailures.length} Fehler`}
            </div>
          )}

          {(pendingCount > 0 || showOfflineHint) && (
            <div
              className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${
                showOfflineHint
                  ? "bg-amber-100 text-amber-800"
                  : "bg-bauwerk-teal/10 text-bauwerk-teal"
              }`}
              data-testid="offline-queue-header-badge"
            >
              {showOfflineHint ? (
                <CloudOff className="w-3.5 h-3.5" />
              ) : offlineQueue.flushing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Clock className="w-3.5 h-3.5" />
              )}
              {pendingCount > 0
                ? pendingCount === 1
                  ? "1 Foto offline"
                  : `${pendingCount} Fotos offline`
                : "Offline"}
            </div>
          )}
        </div>
      </div>

      <div className="px-4 py-4 pb-32">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-bauwerk-teal break-words leading-tight">
          {project.name}
        </h1>
        {project.address && (
          <p className="text-sm text-gray-600 mt-0.5 break-words">
            {project.address}
          </p>
        )}
      </div>

      {/* Offline-/Warteschlangen-Hinweis */}
      <div className="mb-4">
        <FacadeReportExport projectName={project.name} customerName={project.customer ?? ""}
          address={project.address ?? ""} description={projectNote}
          photos={galleryItems.map((item) => item.kind === "server" ? {
            id: item.id, title: item.photo.displayName?.trim() || item.photo.fileName,
            description: item.photo.description ?? "",
            preview: item.photo.hasAnnotation ? PhotoApi.getAnnotatedUrl(item.photo.id) : PhotoApi.getPhotoUrl(item.photo.id),
          } : {
            id: item.id, title: item.record.fileName, description: item.record.description ?? "", preview: item.objectUrl,
          })} />
      </div>
      {(pendingCount > 0 || showOfflineHint) && (
        <div
          className={`mb-3 rounded-lg border p-3 text-sm flex items-start gap-2 ${
            showOfflineHint
              ? "bg-amber-50 border-amber-200 text-amber-900"
              : "bg-bauwerk-mint/40 border-bauwerk-teal/30 text-bauwerk-teal"
          }`}
          data-testid="offline-queue-status"
        >
          <div className="mt-0.5 shrink-0">
            {showOfflineHint ? (
              <CloudOff className="w-4 h-4" />
            ) : offlineQueue.flushing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CloudUpload className="w-4 h-4" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {pendingCount > 0 ? (
              <>
                <div className="font-medium">
                  {pendingCount === 1
                    ? "1 Foto wartet auf Upload"
                    : `${pendingCount} Fotos warten auf Upload`}
                </div>
                <div className="text-xs opacity-80">
                  {showOfflineHint
                    ? "Du bist offline. Wird automatisch hochgeladen, sobald wieder Internet da ist."
                    : offlineQueue.flushing
                    ? "Werden gerade hochgeladen…"
                    : "Werden hochgeladen, sobald die Verbindung steht."}
                </div>
              </>
            ) : (
              <>
                <div className="font-medium">Du bist offline</div>
                <div className="text-xs opacity-80">
                  Neue Fotos werden lokal gespeichert und automatisch hochgeladen, sobald wieder Internet da ist.
                </div>
              </>
            )}
            {pendingCount > 0 && offlineQueue.online && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleManualSync}
                disabled={offlineQueue.flushing}
                className="mt-2 h-8 text-xs bg-white/80"
                data-testid="button-mobile-flush-queue"
              >
                {offlineQueue.flushing ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                )}
                Jetzt synchronisieren
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Foto-Erfassung */}
      <div className="grid grid-cols-1 gap-2 mb-3">
        <Button
          type="button"
          onClick={openCamera}
          disabled={isUploading}
          className="bg-bauwerk-orange hover:bg-bauwerk-light-orange py-4 h-auto text-base shadow-md"
          data-testid="button-mobile-take-photo"
        >
          <Camera className="w-5 h-5 mr-2" />
          Foto aufnehmen
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={openGallery}
          disabled={isUploading}
          className="py-3 h-auto text-base"
          data-testid="button-mobile-upload-gallery"
        >
          <Upload className="w-5 h-5 mr-2" />
          Mehrere aus Galerie
        </Button>
      </div>

      {/* Sammel-Upload-Fortschritt */}
      {uploadProgress && (
        <Card
          className="mb-3 border-bauwerk-teal/30 bg-bauwerk-mint/20"
          data-testid="card-mobile-upload-progress"
        >
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-bauwerk-teal flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {uploadProgress.phase === "compress"
                  ? "Bereite Fotos vor …"
                  : "Lade Fotos hoch …"}
              </span>
              <span
                className="text-bauwerk-teal tabular-nums"
                data-testid="text-mobile-upload-progress-count"
              >
                {Math.min(uploadProgress.inFlightIndex + 1, uploadProgress.total)}/
                {uploadProgress.total}
              </span>
            </div>
            <Progress
              value={
                uploadProgress.total === 0
                  ? 0
                  : (uploadProgress.completed / uploadProgress.total) * 100
              }
              className="h-2"
              data-testid="progress-mobile-upload"
            />
            <div className="text-xs text-gray-600 truncate">
              {uploadProgress.currentName}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fehler-Übersicht des letzten Uploads */}
      {!isUploading && uploadFailures.length > 0 && (
        <Card
          className="mb-3 border-red-300 bg-red-50"
          data-testid="card-mobile-upload-failures"
        >
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-red-700 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2" />
                {uploadFailures.length}{" "}
                {uploadFailures.length === 1 ? "Foto" : "Fotos"} fehlgeschlagen
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setUploadFailures([])}
                className="h-7 w-7 text-red-700 hover:bg-red-100"
                aria-label="Fehlerliste schließen"
                data-testid="button-mobile-upload-failures-close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <ul className="space-y-1 text-xs text-red-800">
              {uploadFailures.map((f, idx) => (
                <li
                  key={`${f.name}-${idx}`}
                  className="break-words"
                  data-testid={`text-mobile-upload-failure-${idx}`}
                >
                  <span className="font-medium">{f.name}</span>: {f.error}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          cameraOpenedRef.current = false;
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {/* Foto-Galerie */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold text-bauwerk-teal">
            Fotos
          </h2>
          {galleryItems.length > 0 && (
            <span className="text-xs text-gray-500">
              {galleryItems.length} {galleryItems.length === 1 ? "Foto" : "Fotos"}
              {pendingCount > 0 && (
                <span className="ml-1 text-amber-700">
                  ({pendingCount} wartet)
                </span>
              )}
            </span>
          )}
        </div>
        {photosLoading && galleryItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
            Fotos laden…
          </div>
        ) : galleryItems.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <ImagePlus className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600">
                Noch keine Fotos. Tippe oben auf „Foto aufnehmen".
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {galleryItems.map((item) => {
              if (item.kind === "server") {
                const photo = item.photo;
                const hasDesc =
                  typeof photo.description === "string" &&
                  photo.description.trim().length > 0;
                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => setOpenItemKey(item.id)}
                    className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 active:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal"
                    data-testid={`mobile-photo-thumb-${photo.id}`}
                  >
                    <img
                      src={photoDisplayUrl(photo)}
                      alt={photo.fileName}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    {hasDesc && (
                      <div className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-bauwerk-teal/90 text-white flex items-center justify-center shadow">
                        <Check className="w-3.5 h-3.5" />
                      </div>
                    )}
                  </button>
                );
              }
              const record = item.record;
              const hasDesc = record.description.trim().length > 0;
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => setOpenItemKey(item.id)}
                  className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 active:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                  data-testid={`mobile-pending-thumb-${record.id}`}
                >
                  {item.objectUrl ? (
                    <img
                      src={item.objectUrl}
                      alt={record.fileName}
                      loading="lazy"
                      className="w-full h-full object-cover opacity-90"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200" />
                  )}
                  <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-amber-500/95 text-white text-[10px] font-medium flex items-center gap-1 shadow">
                    <Clock className="w-3 h-3" />
                    Wartet
                  </div>
                  {hasDesc && (
                    <div className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-bauwerk-teal/90 text-white flex items-center justify-center shadow">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Projekt-Notiz */}
      <div className="mb-6">
        <Label
          htmlFor="m-project-note"
          className="text-base font-semibold text-bauwerk-teal mb-2 block"
        >
          Beschreibung Objekt
        </Label>
        <Textarea
          id="m-project-note"
          placeholder="Allgemeine Notizen zum Objekt …"
          value={projectNote}
          onChange={(e) => setProjectNote(e.target.value)}
          rows={4}
          className="text-base"
          data-testid="textarea-mobile-project-note"
        />
        <div className="mt-2">
          <VoiceInputButton
            value={projectNote}
            onChange={setProjectNote}
            testIdPrefix="voice-mobile-project-note"
            label="Notiz diktieren"
            cleanupContext="project-note"
          />
        </div>
        <Button
          type="button"
          onClick={() => saveNoteMutation.mutate(projectNote)}
          disabled={!noteDirty || savingNote}
          className="mt-2 w-full bg-bauwerk-teal hover:bg-bauwerk-teal/90 py-3 h-auto"
          data-testid="button-mobile-save-note"
        >
          {savingNote ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Speichern…
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Notiz speichern
            </>
          )}
        </Button>
      </div>

      <Card className="bg-bauwerk-mint/30 border-bauwerk-teal/20">
        <CardContent className="p-4 text-sm text-bauwerk-teal">
          Detail-Kalkulation, Leistungsverzeichnis und Angebot bearbeitest du am
          Computer.
        </CardContent>
      </Card>

      {/* Foto-Detail-Dialog */}
      {openItem && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex flex-col"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between px-3 py-2 bg-black/60 text-white">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setOpenItemKey(null)}
              className="text-white hover:bg-white/10 h-11 w-11"
              aria-label="Detailansicht schließen"
              data-testid="button-mobile-photo-close"
            >
              <X className="w-6 h-6" />
            </Button>
            <span className="text-xs truncate px-2">
              {openItem.kind === "server"
                ? openItem.photo.fileName
                : openItem.record.fileName}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() =>
                setDeleteTarget(
                  openItem.kind === "server"
                    ? { kind: "server", id: openItem.photo.id }
                    : { kind: "pending", id: openItem.record.id },
                )
              }
              className="text-red-300 hover:text-red-200 hover:bg-white/10 h-11 w-11"
              aria-label={
                openItem.kind === "server"
                  ? "Foto löschen"
                  : "Aus Warteschlange entfernen"
              }
              data-testid="button-mobile-photo-delete"
            >
              <Trash2 className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex-1 overflow-auto bg-black">
            <img
              src={
                openItem.kind === "server"
                  ? photoDisplayUrl(openItem.photo)
                  : openItem.objectUrl
              }
              alt={
                openItem.kind === "server"
                  ? openItem.photo.fileName
                  : openItem.record.fileName
              }
              className="w-full h-auto max-h-[55vh] object-contain mx-auto"
            />
            <div className="bg-white p-4 space-y-3">
              {openItem.kind === "pending" && (
                <div
                  className="flex items-start gap-2 text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-900 p-2"
                  data-testid="mobile-photo-pending-banner"
                >
                  <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Dieses Foto liegt lokal in der Warteschlange und wird
                    hochgeladen, sobald wieder Internet da ist.
                  </span>
                </div>
              )}
              {openItem.kind === "server" && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAnnotatingPhotoId(openItem.photo.id)}
                    className="w-full py-3 h-auto text-base border-bauwerk-orange text-bauwerk-orange hover:bg-bauwerk-orange/10 hover:text-bauwerk-orange"
                    data-testid="button-mobile-photo-annotate"
                  >
                    <Pencil className="w-5 h-5 mr-2" />
                    {openItem.photo.hasAnnotation
                      ? "Markierungen bearbeiten"
                      : "Markieren"}
                  </Button>
                  {openItem.photo.hasAnnotation && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setRemoveAnnotationTargetId(openItem.photo.id)
                      }
                      className="w-full text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                      data-testid="button-mobile-photo-remove-annotation"
                    >
                      <Eraser className="w-4 h-4 mr-2" />
                      Markierungen entfernen
                    </Button>
                  )}
                </>
              )}
              <Label
                htmlFor="m-photo-desc"
                className="text-sm font-medium text-gray-800"
              >
                Beschreibung dieses Fotos
              </Label>
              <Textarea
                id="m-photo-desc"
                rows={4}
                placeholder="Was ist auf diesem Foto zu sehen? Schäden, Maße, Ort …"
                value={draftDesc}
                onChange={(e) => setDraftDesc(e.target.value)}
                className="text-base"
                data-testid="textarea-mobile-photo-desc"
              />
              <VoiceInputButton
                value={draftDesc}
                onChange={setDraftDesc}
                testIdPrefix="voice-mobile-photo-desc"
                label="Beschreibung diktieren"
                cleanupContext="photo-description"
              />
              <Button
                type="button"
                onClick={() => {
                  if (openItem.kind === "server") {
                    updatePhotoMutation.mutate({
                      id: openItem.photo.id,
                      description: draftDesc,
                    });
                  } else {
                    handleSavePendingDescription(openItem.record, draftDesc);
                  }
                }}
                disabled={
                  openItem.kind === "server"
                    ? updatePhotoMutation.isPending
                    : savingDraftDesc
                }
                className="w-full bg-bauwerk-orange hover:bg-bauwerk-light-orange py-4 h-auto text-base"
                data-testid="button-mobile-photo-save"
              >
                {(openItem.kind === "server"
                  ? updatePhotoMutation.isPending
                  : savingDraftDesc) ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Speichern…
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Beschreibung speichern
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {annotatingPhotoId !== null && (() => {
        const target = photos.find((p) => p.id === annotatingPhotoId);
        if (!target) return null;
        return (
          <PhotoAnnotator
            imageSrc={photoDisplayUrl(target)}
            saving={saveAnnotationMutation.isPending}
            onCancel={() => {
              if (!saveAnnotationMutation.isPending) {
                setAnnotatingPhotoId(null);
              }
            }}
            onSave={async (dataUrl) => {
              await saveAnnotationMutation.mutateAsync({
                id: target.id,
                dataUrl,
              });
            }}
          />
        );
      })()}

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deletePhotoMutation.isPending) {
            setDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.kind === "pending"
                ? "Foto aus Warteschlange entfernen"
                : "Foto löschen"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.kind === "pending"
                ? "Dieses Foto wurde noch nicht hochgeladen. Wenn du es entfernst, geht es endgültig verloren."
                : "Soll dieses Foto wirklich entfernt werden? Diese Aktion kann nicht rückgängig gemacht werden."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePhotoMutation.isPending}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (!deleteTarget) return;
                if (deleteTarget.kind === "server") {
                  deletePhotoMutation.mutate(deleteTarget.id);
                } else {
                  handleRemovePending(deleteTarget.id);
                }
              }}
              disabled={deletePhotoMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletePhotoMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Lösche…
                </>
              ) : (
                "Löschen"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={removeAnnotationTargetId !== null}
        onOpenChange={(open) => {
          if (!open && !removeAnnotationMutation.isPending) {
            setRemoveAnnotationTargetId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Markierungen entfernen</AlertDialogTitle>
            <AlertDialogDescription>
              Sollen die Markierungen wirklich entfernt werden? Das
              Original-Foto bleibt erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeAnnotationMutation.isPending}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (removeAnnotationTargetId !== null) {
                  removeAnnotationMutation.mutate(removeAnnotationTargetId);
                }
              }}
              disabled={removeAnnotationMutation.isPending}
              data-testid="button-mobile-photo-remove-annotation-confirm"
            >
              {removeAnnotationMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Entferne…
                </>
              ) : (
                "Entfernen"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
}
